import * as Yup from 'yup';
import PropTypes from 'prop-types';
import React from 'react';
import { useBoolean } from 'usehooks-ts';
import { useFormik } from 'formik';
import { useLocation, Link, useHistory } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Card from 'components/App/Card';
import ModalPromotionSummarize from 'components/Modals/ModalPromotionSummarize';
import ModalSuccess from 'components/Modals/ModalSuccess';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import AppInputValidation from 'components/App/InputValidation';

import INITIAL_VALUE from 'constants/initialValue';
import { LOCATIONS, LOGIN_TYPE, storageKey, themeColor } from 'constants/index';
import { PROMOTION_CATEGORY, PROMOTION_TYPE } from 'constants/promotion';
import cn from 'utils/classnames';
import { createPromotionValidation } from 'utils/validate';

import { ReactComponent as MarketingPromotionIcon1 } from 'assets/images/marketing-promotion-1.svg';
import { convertIsoDateToString } from 'utils';

import styles from './CreateMarketingPromotion.module.scss';

const BREADCRUMB = [
  {
    name: 'Chương trình khuyến mãi',
    isActive: false,
    link: '',
  },
  {
    name: 'Tạo chương trình khuyến mãi',
    isActive: false,
    link: LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION,
  },
  {
    name: 'Ưu đãi tiếp thị',
    isActive: true,
    link: '',
  },
];

function CreateMarketingPromotion(props) {
  const {
    isLoadingCreatePromotion,
    promotionDetail,
    usableMarketingPromotionDetail,

    actionGetPromotionDetail,
    actionCreatePlacePromotion,
    actionClearDataPromotionDetail,
    actionGetUsableMarketingPromotionDetail,
    actionClearUsableMarketingPromotionDetail,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');
  const promotionId = new URLSearchParams(location.search).get('promotion');
  const marketingPromotionId = new URLSearchParams(location.search).get('marketingPromotion');

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const {
    value: isOpenModal,
    setTrue: onOpenModal,
    setFalse: onCloseModal,
  } = useBoolean(false);

  const {
    value: isOpenSuccessModal,
    setTrue: onOpenSuccessModal,
    setFalse: onCloseSuccessModal,
  } = useBoolean(false);

  const validation = useFormik({
    initialValues: {
      discountRate: '',
      name: '',
    },

    validationSchema: Yup.object({
      discountRate: createPromotionValidation.discountRate,
      name: createPromotionValidation.name,
    }),

    onSubmit: () => {
      onOpenModal();
    },
  });

  const onCallback = React.useCallback(() => {
    onCloseModal();
    onOpenSuccessModal();
  }, [onCloseModal, onOpenSuccessModal]);

  const handleCloseSuccessModal = React.useCallback(() => {
    onCloseSuccessModal();
    history.push({
      pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
      search: `?place=${placeId}&status=GOING`,
    });
  }, [placeId, history, onCloseSuccessModal]);

  const buildPromotionPayload = React.useCallback(
    () => ({
      id: promotionId || undefined,
      place: placeId,
      name: validation.values.name,
      category: PROMOTION_CATEGORY.LIMITED,
      type: PROMOTION_TYPE.MARKETING,
      marketingPromotion: promotionId
        ? promotionDetail.marketingPromotion
        : marketingPromotionId,
      discount: Number(validation.values.discountRate),
    }),
    [
      placeId,
      promotionId,
      promotionDetail.marketingPromotion,
      marketingPromotionId,
      validation.values,
    ],
  );

  const onSubmitCreatePromotion = React.useCallback(() => {
    const promotion = buildPromotionPayload();

    actionCreatePlacePromotion({
      promotion,
      promotionId,
      callback: onCallback,
    });
  }, [
    buildPromotionPayload,
    actionCreatePlacePromotion,
    promotionId,
    onCallback,
  ]);

  React.useEffect(() => {
    if (promotionId && placeId) {
      actionGetPromotionDetail({ placeId, promotionId });
    }

    return () => {
      actionClearDataPromotionDetail();
    };
  }, [
    promotionId,
    placeId,
    actionGetPromotionDetail,
    actionClearDataPromotionDetail,
  ]);

  React.useEffect(() => {
    if (marketingPromotionId) {
      actionGetUsableMarketingPromotionDetail(marketingPromotionId);
    }

    return () => {
      actionClearUsableMarketingPromotionDetail();
    };
  }, [
    marketingPromotionId,
    actionGetUsableMarketingPromotionDetail,
    actionClearUsableMarketingPromotionDetail,
  ]);

  React.useEffect(() => {
    if (Object.keys(promotionDetail).length > 0) {
      validation.setValues({
        discountRate: promotionDetail.discount ?? '',
        name: promotionDetail.name || '',
      });
    }

    return () => {
      validation.resetForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionDetail]);

  const bookingDate = promotionId
    ? promotionDetail.bookingDate
    : usableMarketingPromotionDetail.bookingDate;

  return (
    <div className={cn('page-content', styles.Page)}>
      <Breadcrumb pageTitle="Tạo Ưu Đãi Tiếp Thị" breadcrumbList={BREADCRUMB} />

      <div className={styles.Wrapper}>
        <Card className={styles.Section}>
          <div className={styles.SectionTop}>
            <MarketingPromotionIcon1 className={styles.Icon} />

            <div className={styles.SectionTopTitle}>
              <h3>Tạo Ưu Đãi Tiếp Thị</h3>
              <p>
                Đưa chỗ nghỉ của Quý vị vào các vị trí hiển thị nổi bật trên
                Bookese và các kênh truyền thông phù hợp.
              </p>
            </div>
          </div>
        </Card>

        <Card className={styles.Section}>
          <h3 className={styles.Title}>Thông tin chi tiết về khuyến mãi</h3>

          <div className={styles.FormField}>
            <p className={styles.FormFieldTitle}>Giảm giá</p>
            <Row>
              <Col xs={5}>
                <AppInputNumberValidation
                  name="discountRate"
                  keyName="discountRate"
                  placeholder="Nhập mức giảm giá"
                  thousandSeparator=""
                  validation={validation}
                  onBlur={validation.handleBlur}
                  showCurrency="%"
                  formatOptions={{ decimalScale: 0 }}
                />
              </Col>
            </Row>
            <p className={styles.CombineText}>
              <strong>Kết hợp được với: </strong>
              Kết hợp với nhóm khuyến mãi Phổ biến và Tài khoản
            </p>
          </div>

          <div className={styles.SummaryPromotion}>
            <p className={styles.FormFieldTitle}>
              Giảm giá sẽ được áp dụng cho các lưu trú trong khoảng thời gian sau:
            </p>

            {bookingDate?.from && bookingDate?.to && (
              <span className={styles.FormFieldSubTitle}>
                {`${convertIsoDateToString(bookingDate.from, 'D [tháng] M yyyy')} – ${convertIsoDateToString(bookingDate.to, 'D [tháng] M yyyy')}`}
              </span>
            )}

          </div>
        </Card>

        <Card className={styles.Section}>
          <h3 className={styles.Title}>Tên khuyến mãi</h3>

          <div className={styles.FormField}>
            <div className={styles.FormFieldTitleWrapper}>
              <p className={styles.FormFieldTitle}>
                Quý vị muốn đặt tên khuyến mãi này là gì?
              </p>

              <span className={styles.FormFieldSubTitle}>
                Tên này chỉ dành cho Quý vị. Chúng tôi sẽ không hiển thị tên cho
                khách trên Bookese.com.
              </span>
            </div>

            <AppInputValidation
              type="text"
              id="name"
              name="name"
              validation={validation}
              placeholder="Nhập tên khuyến mãi"
            />
          </div>
        </Card>

        <div className={isExtranet ? styles.StickyButtonsExtranet : styles.StickyButtons}>
          <div className={styles.ButtonWrap}>
            <Link
              to={{
                pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
                search: `?place=${placeId}`,
              }}
            >
              <Button color={themeColor.SECONDARY}>Hủy</Button>
            </Link>

            <Button
              onClick={validation.handleSubmit}
              disabled={promotionId && promotionDetail.isEditable === false}
            >
              {promotionId ? 'Cập nhật' : 'Tạo'}
            </Button>
          </div>
        </div>

        <ModalPromotionSummarize
          isOpen={isOpenModal}
          loading={isLoadingCreatePromotion}
          validation={validation}
          isSimpleSummary
          onCloseClick={onCloseModal}
          onSubmit={onSubmitCreatePromotion}
        />

        <ModalSuccess
          isOpen={isOpenSuccessModal}
          onCloseModal={handleCloseSuccessModal}
          message={promotionId ? 'Bạn đã cập nhật khuyến mãi thành công' : 'Bạn đã tạo khuyến mãi thành công'}
        />
      </div>
    </div>
  );
}

CreateMarketingPromotion.propTypes = {
  isLoadingCreatePromotion: PropTypes.bool.isRequired,
  promotionDetail: PropTypes.instanceOf(Object),
  usableMarketingPromotionDetail: PropTypes.instanceOf(Object),

  actionGetPromotionDetail: PropTypes.func.isRequired,
  actionCreatePlacePromotion: PropTypes.func.isRequired,
  actionClearDataPromotionDetail: PropTypes.func.isRequired,
  actionGetUsableMarketingPromotionDetail: PropTypes.func.isRequired,
  actionClearUsableMarketingPromotionDetail: PropTypes.func.isRequired,
};

CreateMarketingPromotion.defaultProps = {
  promotionDetail: INITIAL_VALUE.OBJECT,
  usableMarketingPromotionDetail: INITIAL_VALUE.OBJECT,
};

export default CreateMarketingPromotion;
